import { editorGuidKeyName } from "@needle-tools/engine";
import { Emitter, Event } from "../../../base/common/events";
import { Disposable } from "../../../base/common/lifecycle";
import { EditorWindow, IEditorWindow } from "../../window/browser/window";
import { IOpenConfiguration, IWindowsMainService } from "../common/windows";
import { IAnalyticsService } from "../../analytics/common/analytics";
import { getWindowId, registerWindow } from "../../../base/browser/dom";
import { ensureCodeWindow } from "../../../base/browser/window";
import { ILogService } from "../../log/common/log";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage";
import { INotificationService } from "../../notifications/common/notifications";
import { Delayer } from "../../../base/common/async";

let _windowIds = 0;

export class WindowsMainService extends Disposable implements IWindowsMainService {
    _serviceBrand: undefined;

    readonly allowPopout: boolean = false;

    private readonly _onDidOpenWindow = this._register(new Emitter<IEditorWindow>());
    readonly onDidOpenWindow: Event<IEditorWindow> = this._onDidOpenWindow.event;

    private readonly _onDidDestroyWindow = this._register(new Emitter<IEditorWindow>());
    readonly onDidDestroyWindow: Event<IEditorWindow> = this._onDidDestroyWindow.event;

    private mainWindow: IEditorWindow | null = null;
    private windows: IEditorWindow[] = [];

    constructor(
        config?: { allowPopout: boolean },
        @IAnalyticsService telemetry?: IAnalyticsService,
        @ILogService private readonly logger?: ILogService,
        @IStorageService private readonly storageService?: IStorageService,
        @INotificationService private readonly notificationService?: INotificationService,
    ) {
        super();
        this.allowPopout = config?.allowPopout ?? false;
        ensureCodeWindow(window, 0);
        this.mainWindow = new EditorWindow(globalThis.window, {
            id: String(window.needleWindowId),
            view: "main",
        });
        this.windows.push(this.mainWindow);

        this.mainWindow.onDidClose(() => {
            for (const win of this.windows) {
                if (win !== this.mainWindow) {
                    win.getWindow().close();
                }
            }
            this.windows = [];
        });
    }

    async open(openConfig: IOpenConfiguration): Promise<IEditorWindow[]> {

        const id = openConfig.id;

        if (openConfig.restorePreviousState && id) {
            const state = this.loadWindowState(id);
            if (state) {
                openConfig.expectedPosition = { x: state.x, y: state.y };
                openConfig.expectedSize = { width: state.innerWidth, height: state.innerHeight };
            }
        }

        const width = openConfig.expectedSize?.width ?? 1000;
        const height = openConfig.expectedSize?.height ?? 800;
        const url = openConfig.url ?? "";
        const positionX = openConfig.expectedPosition?.x ?? (globalThis.screen.width - width) / 2;
        const positionY = openConfig.expectedPosition?.y ?? (globalThis.screen.height - height) / 2;
        this.logger?.info(`Opening new window`, openConfig);
        const newWindow = globalThis.open(url, "_blank", `left=${positionX},top=${positionY},width=${width},height=${height},resizable,scrollbars,titlebar=no,menubar=no,toolbar=no,location=no,status=no`);
        if (!newWindow) {
            this.notificationService?.error("Failed to open new window (popup blocked?)");
            throw new Error("Failed to open new window (popup blocked?)");
        }
        newWindow.document.title = openConfig.title || (window.document.title + ' — Needle Inspector');

        const editorWindow = new EditorWindow(
            newWindow, {
            id: id || `window-${++_windowIds}`,
            view: openConfig.view || 'default',
        });
        editorWindow.onDidClose(() => {
            const browserWindow = editorWindow.getWindow();
            const index = this.windows.indexOf(editorWindow);
            if (index >= 0) {
                this.windows.splice(index, 1);
            }
            this._onDidDestroyWindow.fire(editorWindow);
            if (id) {
                this.saveWindowState(id, browserWindow);
            }
        });

        if (id) {
            const delayer = new Delayer(100);
            const requestSaveState = () => {
                delayer.trigger(() => {
                    this.saveWindowState(id, newWindow);
                });
            }
            editorWindow.onDidResize(() => requestSaveState());
            editorWindow.onDidMove(() => requestSaveState());
        }

        this.windows.push(editorWindow);
        this._onDidOpenWindow.fire(editorWindow);

        return [editorWindow];
    }

    getWindows(): IEditorWindow[] {
        return this.windows;
    }


    private loadWindowState(id: string): WindowStateModel | null {
        this.logger?.info(`Restoring window state for id: ${id}`);
        const storedState = this.storageService?.getObject<WindowStateModel>(`window-state-${id}`, StorageScope.PROFILE);
        if (storedState) {
            return storedState;
        }
        return null;
    }

    private saveWindowState(id: string, window: Window) {
        this.logger?.info(`Saving window state for id: ${id}`);
        const state: WindowStateModel = {
            width: window.outerWidth,
            height: window.outerHeight,
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            x: window.screenX,
            y: window.screenY
        };
        this.storageService?.store(`window-state-${id}`, state, StorageScope.PROFILE, StorageTarget.MACHINE);
    }

}

interface WindowStateModel {
    /** Outer width of the window */
    width: number;
    /** Outer height of the window */
    height: number;
    innerWidth: number;
    innerHeight: number;
    x: number;
    y: number;
}